import React, { useEffect, useState, useCallback } from "react";
import { useForm, FormProvider } from "react-hook-form";
import StepDataSource from "../components/Views/PipelineMultiStepForm/StepDataSource";
import StepEnrichment from "../components/Views/PipelineMultiStepForm/StepEnrichment";
import StepFilters from "../components/Views/PipelineMultiStepForm/StepFilters";
import StepTranscription from "../components/Views/PipelineMultiStepForm/StepTranscription";
import StepAnalysisType from "../components/Views/PipelineMultiStepForm/StepAnalysisType";
import StepAnalysisConfig from "../components/Views/PipelineMultiStepForm/StepAnalysisConfig";
import PipelineSummary from "../components/Views/PipelineMultiStepForm/PipelineSummary";
import { Col, CardHeader, Container, Row, Alert } from "reactstrap";
import api from "service/api";
import qs from "query-string";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

const initialState = {
  title: "",
  description: "",
  data_source: "",
  data_source_type: "",
  import_format: "",
  data_source_folder_id: null,
  import_csv: [],
  files: [],
  enrichment_type: "",
  enrichment_query: "",
  enrichment_csv: null,
  filter_type: "",
  filter_query: "",
  filter_event: "",
  filter_csv: null,
  provider: "",
  language_id: "",
  vocabularyLibrary: null,
  analysis_type: "",
  prompts: [],
  libraries: [],
  libraryConfigurations: {},
  segment: null,
  metadata_source_type: "",
  metadata_source_files: [],
  metadataSourceQuery: "",
  metadata_csv_reference: "",
  metadataIsFilter: false,
  is_recurring: false,
  search_pattern: "d-1",
  recurrence_filters: [],
  recurrence_day_of_month: null,
  frequency: "daily",
  selected_days: ["seg", "ter", "qua", "qui", "sex"],
  execution_time: "09:00",
};

function buildFolderDatePattern(folderId, hierarchy) {
  const dateFolderIdx = hierarchy.findIndex((f) => f.dateFormat != null);
  if (dateFolderIdx === -1) return null;
  const dateFolder = hierarchy[dateFolderIdx];
  return {
    folder_id: folderId,
    date_folder_parent_id: dateFolder.parentFolderId,
    date_format: dateFolder.dateFormat,
    relative_child_path: hierarchy
      .slice(0, dateFolderIdx)
      .reverse()
      .map((f) => f.name),
  };
}

function PipelineMultiStepForm() {
  const [step, setStep] = useState(1);
  const [isCopy, setIsCopy] = useState(false);

  const methods = useForm({
    defaultValues: initialState,
    mode: "onSubmit",
    shouldUnregister: false,
  });

  const history = useHistory();
  const { handleSubmit, getValues, setError, clearErrors, setValue } = methods;
  const data_source = getValues("data_source");
  const metadata_is_filter = getValues("metadataIsFilter");
  const data_source_type = getValues("data_source_type");
  const metadata_csv_reference = getValues("metadata_csv_reference");

  const next = () => setStep((s) => s + 1);
  const back = () =>
    setStep((s) => {
      if (metadata_is_filter) {
        if (s === 4 || s === 5) {
          return 3;
        }
      }

      if (data_source === "CLOUD") {
        if (data_source_type === "AUDIO" && s === 4) {
          return s - 2;
        }
        if (data_source_type === "csv" && s === 5) {
          return s - 3;
        }
      }

      if (data_source === "LOCAL" && data_source_type !== "AUDIO" && s === 5) {
        return s - 4;
      }
      if (data_source === "LOCAL" && s === 4) {
        return s - 3;
      }
      return s - 1;
    });

  const onSubmit = async (data) => {
    let hasError = false;

    if (!data.title || data.title.trim() === "") {
      setError("title", {
        type: "manual",
        message: "Título da pipeline é obrigatório.",
      });
      hasError = true;
    }
    if (!data.segment || !data.segment.id) {
      setError("segment", {
        type: "manual",
        message: "Segmento é obrigatório.",
      });
      hasError = true;
    }

    if (data.analysis_type === "generative") {
      if (!data.segment || !data.segment.id) {
        setError("segment", {
          type: "manual",
          message: "Seleção de segmento é obrigatória.",
        });
        hasError = true;
      }
      if (
        !data.prompts ||
        !Array.isArray(data.prompts) ||
        data.prompts.length === 0
      ) {
        setError("prompts", {
          type: "manual",
          message: "Seleção de prompt é obrigatória.",
        });
        hasError = true;
      }
    }
    if (data.analysis_type === "library-matching") {
      if (!data.libraries || data.libraries.length === 0) {
        setError("libraries", {
          type: "manual",
          message: "Selecione ao menos uma biblioteca.",
        });
        hasError = true;
      }

      const libs = data.libraries || [];
      const cfg = data.libraryConfigurations || {};
      if (libs.length > 0) {
        const missing = libs.filter((lib) => !cfg[lib.value]);
        if (missing.length > 0) {
          setError("libraryConfigurations", {
            type: "manual",
            message: "Configuração de todas as bibliotecas é obrigatória.",
          });
          hasError = true;
        }
      }
    }

    if (hasError) return;

    clearErrors([
      "title",
      "segment",
      "prompts",
      "libraries",
      "libraryConfigurations",
    ]);

    let pipelineConfig = {};

    // const filteredFiles = data.filtered_files || [];
    // const hasFilteredFiles = filteredFiles.length > 0;

    const storageSource =
      data.data_source === "CLOUD"
        ? data?.folders?.flatMap((folder) => {
            const base = {
              type: data.data_source_type === "AUDIO" ? "AUDIO" : "TEXT",
              origin: data.data_source,
              folder_id: folder,
              // event_source_files: data.event_sources_files,
            };

            // if (hasFilteredFiles) {
            //   base.file = filteredFiles;
            // }

            const metadata =
              data.metadata_source_type === "csv" || data.metadata_source_data
                ? [
                    {
                      type: "TEXT_METADATA",
                      origin: data.data_source,
                      folder_id: folder,
                    },
                  ]
                : [];

            return [base, ...metadata];
          })
        : [
            {
              type: data.data_source_type === "AUDIO" ? "AUDIO" : "TEXT",
              origin: data.data_source,
              folder_id: data.data_source_folder_id,
              // ...(hasFilteredFiles && { file: filteredFiles }),
            },
          ];

    if (data.analysis_type === "generative") {
      pipelineConfig = {
        client_id: data.client.id,
        language_id: data.language.id,
        prompts: data.prompts,
        segment_id: data.segment.id,
        title: data.title,
        description: data.description,
        provider: data?.provider || null,
        metadata_is_filter: data?.metadataIsFilter ?? false,
        query: data?.metadataSourceQuery ? data.metadataSourceQuery : null,
        storage_source: storageSource,
        vocabulary_library_id: data?.vocabularyLibrary?.id || null,
      };
    }

    if (data.analysis_type === "library-matching") {
      pipelineConfig = {
        client_id: data.client.id,
        language_id: data.language.id,
        segment_id: data.segment.id,
        title: data.title,
        description: data.description,
        provider: data?.provider || null,
        metadata_is_filter: data?.metadataIsFilter ?? false,
        query: data?.metadataSourceQuery ? data.metadataSourceQuery : null,
        storage_source: storageSource,
        vocabulary_library_id: data?.vocabularyLibrary?.id || null,
        libraries: data?.libraries?.map((library) => {
          return {
            id: library?.value,
            ...data.libraryConfigurations[library?.value],
          };
        }),
      };
      pipelineConfig.metadata_filters = [];

      if (data.metadata_filter_pools && data.metadata_filter_pools.length > 0) {
        if (data.metadata_records_limit) {
          pipelineConfig.metadata_filters = [
            {
              limit: data.metadata_records_limit,
              operator: "LIMIT",
              value: "limit",
              column: "global",
            },
            ...data.metadata_filter_pools,
          ];
        } else {
          pipelineConfig.metadata_filters = data.metadata_filter_pools;
        }
      }
    }

    if (data.metadata_filter_pools) {
      pipelineConfig.metadata_filters = data.metadata_filter_pools;
    }

    if (data.metadata_records_limit) {
      const globalLimitFilter = {
        limit: data.metadata_records_limit,
        operator: "LIMIT",
        value: "limit",
        column: "global",
      };

      if (pipelineConfig.metadata_filters) {
        pipelineConfig.metadata_filters.unshift(globalLimitFilter);
      } else {
        pipelineConfig.metadata_filters = [globalLimitFilter];
      }
    }

    if (metadata_csv_reference?.trim()) {
      pipelineConfig["metadata_csv"] = {
        reference: metadata_csv_reference.trim(),
        folder_id: data.data_source_folder_id,
      };
    }

    if (data.metadata_filter_result_cache_key) {
      pipelineConfig.metadata_filter_result_cache_key =
        data.metadata_filter_result_cache_key;
    }

    if (data.event_distribution_column) {
      pipelineConfig.event_distribution_column = data.event_distribution_column;
    }
    if (data.event_distribution_limit) {
      pipelineConfig.event_distribution_limit = Number(
        data.event_distribution_limit,
      );
    }

    if (data.is_recurring) {
      const [hourStr, minuteStr] = (data.execution_time || "09:00").split(":");
      const WEEKDAY_MAP = {
        dom: 0,
        seg: 1,
        ter: 2,
        qua: 3,
        qui: 4,
        sex: 5,
        sab: 6,
      };
      const FREQ_MAP = { daily: "DAILY", weekly: "WEEKLY", monthly: "MONTHLY" };

      pipelineConfig.is_recurrent = true;
      pipelineConfig.recurrence_frequency = FREQ_MAP[data.frequency] || "DAILY";
      pipelineConfig.recurrence_search_pattern = data.search_pattern;
      // Convert local time to UTC before sending to backend
      const localDate = new Date();
      localDate.setHours(parseInt(hourStr, 10), parseInt(minuteStr, 10), 0, 0);
      pipelineConfig.recurrence_hour = localDate.getUTCHours();
      pipelineConfig.recurrence_minute = localDate.getUTCMinutes();

      if (data.frequency === "weekly") {
        pipelineConfig.recurrence_days_of_week = (data.selected_days || [])
          .map((d) => WEEKDAY_MAP[d])
          .filter((n) => n !== undefined);
      }
      if (data.frequency === "monthly" && data.recurrence_day_of_month) {
        pipelineConfig.recurrence_day_of_month = Number(
          data.recurrence_day_of_month,
        );
      }

      if (data.data_source === "CLOUD") {
        const selectedFolderIds = data.folders || [];
        const folderDatePatterns = [];
        const cachedMap =
          data.CachedFolders instanceof Map ? data.CachedFolders : new Map();

        let dateFolderContext = null;

        for (const folderId of selectedFolderIds) {
          const hierarchy = await getFolderHierarchy(folderId);
          const pattern = buildFolderDatePattern(folderId, hierarchy);
          if (pattern) {
            folderDatePatterns.push(pattern);
            if (!dateFolderContext) {
              const dateFolderIdx = hierarchy.findIndex(
                (f) => f.dateFormat != null,
              );
              if (dateFolderIdx !== -1) {
                const df = hierarchy[dateFolderIdx];
                dateFolderContext = {
                  id: df.id,
                  date_folder_parent_id: df.parentFolderId,
                  date_format: df.dateFormat,
                };
              }
            }
          }
        }

        const recurrenceFilters = data.recurrence_filters || [];
        if (recurrenceFilters.length > 0 && dateFolderContext) {
          const dateFolderChildren = cachedMap.get(dateFolderContext.id) || [];
          for (const filterName of recurrenceFilters) {
            const child = dateFolderChildren.find(
              (c) => (c.label || c.name) === filterName,
            );
            if (child?.id) {
              folderDatePatterns.push({
                folder_id: child.id,
                date_folder_parent_id: dateFolderContext.date_folder_parent_id,
                date_format: dateFolderContext.date_format,
                relative_child_path: [filterName],
              });
            }
          }
        }

        pipelineConfig.folder_date_patterns = folderDatePatterns;
      } else {
        pipelineConfig.folder_date_patterns = [];
      }
    }

    api
      .post("/pipeline", pipelineConfig)
      .then((response) => {
        if (!response?.data?.id) {
          toast.error("Houve um problema ao cadastrar a pipeline.");
          return;
        }
        toast.success("Configuração de pipeline armazenada com sucesso.");
        history.push(`/admin/pipeline/${response.data.id}`);
      })
      .catch((error) => {
        console.error("Error creating pipeline:", error);
        toast.error("Erro ao criar a pipeline.");
      });
  };

  const getParamsId = () => {
    const queryParams = qs.parse(window.location.search);
    const id = queryParams.id;
    return id ? id : undefined;
  };

  useEffect(() => {
    const id = getParamsId();
    if (id) {
      api
        .get(`/pipeline/${id}/configuration`)
        .then((response) => {
          if (response?.data) {
            console.log("Pipeline data fetched:", response.data);
            setIsCopy(true);
            setValue("IsCopy", true);
            if (response.data.client && response.data.client.id) {
              setValue("client", {
                id: response.data.client.id,
                name: response.data.client.name,
              });
            }
            if (
              response.data.pipelineStorageSourcePath &&
              response.data.pipelineStorageSourcePath.length > 0
            ) {
              const type = response.data.pipelineStorageSourcePath[0].type;
              const origin = response.data.pipelineStorageSourcePath[0].origin;
              const folders = response.data.pipelineStorageSourcePath.map(
                (path) => path.folder_id,
              );
              const metadataSourceType = response.data.query ? "query" : "csv";

              let dataSourceType = type;
              if (origin === "LOCAL") {
                dataSourceType = type === "TEXT" ? "csv" : type;
              }

              setValue("data_source_type", dataSourceType);
              setValue("data_source", origin);
              if (origin !== "LOCAL") {
                validateAndSetFolders(folders);
                setValue("CachedSelectedItems", folders);
              }

              // metadata
              setValue("metadataIsFilter", response.data.metadataIsFilter);

              if (
                response.data.metadataFilters &&
                response.data.metadataFilters.length > 0
              ) {
                const globalLimitFilter = response.data.metadataFilters.find(
                  (filter) =>
                    filter.column === "global" && filter.operator === "LIMIT",
                );
                if (globalLimitFilter) {
                  setValue("metadata_records_limit", globalLimitFilter.limit);
                }

                const otherFilters = response.data.metadataFilters.filter(
                  (filter) =>
                    !(
                      filter.column === "global" && filter.operator === "LIMIT"
                    ),
                );
                setValue("metadata_filter_pools", otherFilters);
              } else {
                setValue("metadata_filter_pools", []);
              }
              setValue("metadata_source_type", metadataSourceType);
              if (metadataSourceType === "csv") {
                setValue(
                  "metadata_csv_reference",
                  response.data.metadata_csv?.reference || "",
                );
              }
              if (metadataSourceType === "query") {
                setValue("metadataSourceQuery", response.data.query || "");
              }

              //transcription
              if (type === "AUDIO") {
                setValue("provider", response.data.provider || "");
                setValue("language", {
                  id: response.data.language_id,
                  name: response.data.language_name,
                });

                // Set vocabulary library if it exists
                if (
                  response.data.vocabulary_library_id &&
                  response.data.vocabulary_library_title
                ) {
                  setValue("vocabularyLibrary", {
                    id: response.data.vocabulary_library_id,
                    title: response.data.vocabulary_library_title,
                  });
                }
              }

              // Analyses
              if (
                response.data.pipelinePrompt &&
                response.data.pipelinePrompt.length > 0
              ) {
                setValue("analysis_type", "generative");
                setValue(
                  "prompts",
                  response.data.pipelinePrompt.map((prompt) => prompt.id),
                );
              }
              if (
                response.data.pipelineLibrary &&
                response.data.pipelineLibrary.length > 0
              ) {
                setValue("analysis_type", "library-matching");
                setValue(
                  "libraries",
                  response.data.pipelineLibrary
                    .filter((lib) => lib.library && lib.library.id)
                    .map((lib) => ({
                      value: lib.library.id,
                      label: lib.library?.title || lib.library?.name,
                    })),
                );

                const libraryConfigurations = {};
                response.data.pipelineLibrary.forEach((lib) => {
                  if (!lib.library || !lib.library.id)
                    toast.warn(
                      "Algumas bibliotecas copiadas foram removidas ou não estão mais acessíveis.",
                    );
                  if (lib.library && lib.library.id) {
                    libraryConfigurations[lib.library.id] = {
                      consultant_message:
                        lib.search_consultant_message || false,
                      customer_message: lib.search_customer_message || false,
                      search_type: lib.searchType,
                    };
                  }
                });

                setValue("libraryConfigurations", libraryConfigurations);
              }
              if (!response.data.segment)
                toast.warn("Segmento não encontrado.");
              if (response.data.segment && response.data.segment.id) {
                setValue("segment", {
                  id: response.data.segment.id,
                  name: response.data.segment.title,
                });
              }

              setValue("title", response.data.title || "");
              setValue("description", response.data.description || "");
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching pipeline data:", error);
          toast.error("Erro ao carregar dados da pipeline.");
        });
    }
  }, []);

  const getFolderHierarchy = useCallback(async (folderId) => {
    try {
      const response = await api.get(
        `/event-audios/folders/${folderId}/hierarchy`,
      );
      if (response.status === 200) {
        return response.data || [];
      }
    } catch (error) {
      console.warn(
        `Não foi possível buscar hierarquia da pasta ${folderId}:`,
        error,
      );
    }
    return [];
  }, []);

  const validateAndSetFolders = async (folders) => {
    const validFolders = [];

    for (const folder of folders) {
      const hierarchy = await getFolderHierarchy(folder);
      if (hierarchy.length > 0) {
        validFolders.push(folder);
      }
    }

    setValue("folders", validFolders);
    console.log("Valid folders set:", validFolders);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return <StepDataSource next={next} />;
      case 2:
        return <StepEnrichment next={next} back={back} />;
      case 3:
        return <StepFilters next={next} back={back} />;
      case 4:
        return <StepTranscription next={next} back={back} />;
      case 5:
        return <StepAnalysisType next={next} back={back} />;
      case 6:
        return <StepAnalysisConfig back={back} />;
      default:
        return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <Container className="mt-7" fluid>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <CardHeader className="bg-white border-0">
            <Row className="align-items-center">
              <Col xs="8">
                <h3> Pipeline de Análise </h3>
              </Col>
              <Col className="text-right" xs="4"></Col>
            </Row>
            {isCopy && (
              <Alert
                color="warning"
                style={{
                  display: "inline-block",
                  margin: 0,
                  width: "auto",
                  minWidth: 0,
                }}
              >
                <i className="ni ni-chat-round"></i> <b>Atenção: </b> Você está
                criando esta pipeline com base em uma cópia de outra pipeline
                existente.
              </Alert>
            )}
          </CardHeader>
          <Row>
            <Col md={8}>{renderStep()}</Col>
            <Col md={4}>
              <PipelineSummary step={step} />
            </Col>
          </Row>
        </form>
      </Container>
    </FormProvider>
  );
}

export default PipelineMultiStepForm;
